import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { budgetsApi } from '../../api/facades';
import PageHeader from '../shared/PageHeader';
import Button from '../../components/common/Button';

const formatMkw = (amount: number): string => {
  const formatted = (amount || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `MK${formatted}`;
};

const BudgetDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const budgetId = Number(id);

  const { data: res, isLoading, isError } = useQuery({
    queryKey: ['budget', budgetId],
    enabled: !Number.isNaN(budgetId),
    queryFn: () => budgetsApi.get(budgetId),
  });

  if (Number.isNaN(budgetId) || isError) {
    return (
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
        <p className="text-slate-600 dark:text-slate-400">Budget not found.</p>
        <Button variant="secondary" className="mt-4" onClick={() => navigate('/dashboard/budget')}>
          Back to budgets
        </Button>
      </div>
    );
  }

  const budget = (res as any)?.data?.data ?? {};

  const ceiling = Number(budget.ceiling_amount || 0);
  const spent = Number(budget.spent_amount || 0);
  const remaining = typeof budget.remaining_budget === 'number'
    ? Number(budget.remaining_budget)
    : ceiling - spent;
  const pct = ceiling > 0 ? (spent / ceiling) * 100 : 0;

  return (
    <div className="space-y-4">
      <PageHeader
        hideTitle
        subtitle="Budget details and utilization for the selected year."
      />

      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
        {isLoading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Budget
                </div>
                <div className="mt-1 text-xl font-semibold text-slate-800 dark:text-slate-100">
                  {budget.year} – {budget.department?.department_name || 'All Departments'}
                </div>
                {budget.category && (
                  <div className="text-sm text-slate-500 dark:text-slate-400">
                    {budget.category}
                  </div>
                )}
              </div>
              <Button variant="secondary" onClick={() => navigate('/dashboard/budget')}>
                Back to budgets
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Ceiling
                </div>
                <div className="mt-1 text-2xl font-bold text-slate-800 dark:text-slate-100">
                  {formatMkw(ceiling)}
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Spent
                </div>
                <div className="mt-1 text-2xl font-bold text-slate-800 dark:text-slate-100">
                  {formatMkw(spent)}
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Remaining
                </div>
                <div className="mt-1 text-2xl font-bold text-slate-800 dark:text-slate-100">
                  {formatMkw(remaining)}
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Utilization
                </div>
                <div className="mt-1 text-2xl font-bold text-slate-800 dark:text-slate-100">
                  {pct.toFixed(1)}%
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BudgetDetailPage;

